import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  FingerPrintIcon, CreditCardIcon, AcademicCapIcon,
  ArrowPathIcon, StopIcon, PlayIcon, XMarkIcon,
  PencilSquareIcon, CalendarDaysIcon, ClockIcon,
  UserGroupIcon, ArrowDownTrayIcon, PlusIcon,
} from '@heroicons/react/24/outline';

const fmtTime = d => d ? new Date(d).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:true}) : '—';
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const ini     = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'S';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .fb * { box-sizing:border-box; }
  .fb { font-family:'Sora',sans-serif; color:#1a1d2e; animation:fbFade .4s ease both; }
  @keyframes fbFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .fb-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .fb-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .fb-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-teal  { background:#0d9488; color:white; } .btn-teal:hover { background:#0f766e; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover { background:#ef4444; color:white; }
  .btn-blue  { background:#3b6ef8; color:white; } .btn-blue:hover { background:#2d5ef0; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-amber { background:#fffbeb; color:#d97706; border:1px solid #fde68a; } .btn-amber:hover { background:#d97706; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  /* Tabs */
  .page-tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.4rem; border-bottom:2px solid #f0f2f8; padding-bottom:0; }
  .page-tab  { padding:9px 18px; border:none; background:none; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; color:#9094a8; border-bottom:2px solid transparent; margin-bottom:-2px; transition:all .15s; display:inline-flex; align-items:center; gap:6px; }
  .page-tab svg { width:15px; height:15px; stroke-width:1.8; }
  .page-tab:hover { color:#0d9488; }
  .page-tab.active { color:#0d9488; border-bottom-color:#0d9488; }
  /* Status card */
  .status-card { border-radius:16px; padding:1.2rem 1.4rem; margin-bottom:1.4rem; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:1rem; }
  .status-card.polling      { background:linear-gradient(135deg,#eef2ff,#dbeafe); border:1px solid #c7d2fe; }
  .status-card.connected    { background:linear-gradient(135deg,#ecfdf5,#d1fae5); border:1px solid #a7f3d0; }
  .status-card.disconnected { background:linear-gradient(135deg,#fef2f2,#fee2e2); border:1px solid #fecaca; }
  .sc-left  { display:flex; align-items:center; gap:12px; }
  .sc-dot   { width:12px; height:12px; border-radius:50%; flex-shrink:0; }
  .sc-dot.blue  { background:#3b6ef8; box-shadow:0 0 0 3px rgba(59,110,248,.2); animation:pulse 2s infinite; }
  .sc-dot.green { background:#10c98e; box-shadow:0 0 0 3px rgba(16,201,142,.2); animation:pulse 2s infinite; }
  .sc-dot.red   { background:#ef4444; }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.6} }
  .sc-label { font-size:.9rem; font-weight:700; color:#0f1123; }
  .sc-sub   { font-size:.72rem; color:#6b7194; margin-top:2px; }
  /* Panel */
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .panel-sub   { font-size:.7rem; color:#9094a8; margin-top:1px; }
  /* Table */
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .tbl tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .tbl tbody tr:last-child { border-bottom:none; }
  .tbl tbody tr:hover { background:#fafbff; }
  .tbl td { padding:10px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .mono { font-family:'JetBrains Mono',monospace; font-size:.75rem; color:#6b7194; }
  .rfid-badge { display:inline-flex; align-items:center; gap:4px; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; background:#eef2ff; color:#3b6ef8; font-family:'JetBrains Mono',monospace; }
  .rfid-badge svg { width:11px; height:11px; }
  .no-rfid { font-size:.72rem; color:#c0c5dc; }
  .name-cell { display:flex; align-items:center; gap:9px; }
  .avatar { width:30px; height:30px; border-radius:8px; background:linear-gradient(135deg,#0d9488,#3b6ef8); display:flex; align-items:center; justify-content:center; font-size:.62rem; font-weight:700; color:white; flex-shrink:0; }
  .time-badge { display:inline-flex; align-items:center; gap:4px; font-family:'JetBrains Mono',monospace; font-size:.76rem; font-weight:600; padding:3px 9px; border-radius:99px; background:#ecfdf5; color:#10c98e; }
  .time-badge svg { width:11px; height:11px; }
  /* Stat cards */
  .stat-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.2rem; border:1px solid #e8ebf4; }
  .sc-icon { width:34px; height:34px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:.6rem; }
  .sc-icon svg { width:16px; height:16px; stroke-width:1.8; }
  .sc-icon.teal  { background:#f0fdfa; color:#0d9488; }
  .sc-icon.blue  { background:#eef2ff; color:#3b6ef8; }
  .sc-icon.amber { background:#fffbeb; color:#f59e0b; }
  .sc-lbl { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sc-val { font-size:1.4rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; margin-top:2px; }
  /* Filter */
  .search-wrap { position:relative; max-width:240px; }
  .search-wrap svg { position:absolute; left:10px; top:50%; transform:translateY(-50%); width:14px; height:14px; stroke:#9094a8; pointer-events:none; }
  .search-input { font-family:'Sora',sans-serif; font-size:.78rem; width:100%; padding:7px 10px 7px 30px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; outline:none; }
  .search-input:focus { border-color:#0d9488; background:white; }
  .gl-date { font-family:'JetBrains Mono',monospace; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; }
  .gl-date:focus { border-color:#0d9488; }
  /* Req info box */
  .req-info { background:#f0fdfa; border:1px solid #99f6e4; border-radius:12px; padding:12px 16px; margin-bottom:1rem; font-size:.78rem; color:#134e4a; }
  .req-info strong { color:#0f766e; }
  /* Empty / loading */
  .empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:180px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#0d9488; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  /* Modal */
  .overlay { position:fixed; inset:0; background:rgba(10,12,28,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; width:100%; max-width:460px; max-height:90vh; overflow-y:auto; box-shadow:0 24px 64px rgba(0,0,0,.2); animation:moIn .25s ease; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .mo-hdr { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; }
  .mo-title { font-size:.95rem; font-weight:700; color:#0f1123; }
  .mo-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .mo-close svg { width:14px; height:14px; stroke-width:2; }
  .mo-body { padding:20px 22px; }
  .mo-foot { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; }
  .ff { display:flex; flex-direction:column; gap:5px; margin-bottom:12px; }
  .fl { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fi,.fs { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .fi:focus,.fs:focus { border-color:#0d9488; background:white; }
  .fi.mono { font-family:'JetBrains Mono',monospace; }
  .fh { font-size:.68rem; color:#b0b5cc; }
  .ctx-box { background:#f8f9fc; border-radius:9px; padding:10px 12px; margin-bottom:12px; font-size:.78rem; }
  .ctx-name { font-weight:600; color:#0f1123; } .ctx-sub { color:#9094a8; font-size:.72rem; margin-top:1px; }
  .fb-cards { display:none; padding:10px; }
  .fb-card  { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .fbc-top  { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .fbc-row  { display:flex; justify-content:space-between; align-items:center; padding:5px 0; gap:10px; border-bottom:1px solid #f5f6fb; font-size:.78rem; }
  .fbc-row:last-of-type { border-bottom:none; }
  .fbc-label { color:#9094a8; font-weight:600; font-size:.7rem; text-transform:uppercase; letter-spacing:.05em; white-space:nowrap; }
  .fbc-actions { margin-top:10px; padding-top:8px; border-top:1px solid #f0f2f8; }
  @media (max-width:768px) {
    .stat-grid { grid-template-columns:1fr 1fr; }
    .fb-title  { font-size:1.1rem; }
    .fb-tbl-wrap { display:none !important; }
    .fb-cards { display:block; }
  }
`;

export default function FinanceBiometric() {
  const [activeTab, setActiveTab] = useState('status');
  const [status,    setStatus]    = useState(null);
  const [starting,  setStarting]  = useState(false);
  const [stopping,  setStopping]  = useState(false);

  // RFID Teachers
  const [rfidTeachers, setRFIDTeachers] = useState([]);
  const [teachSearch,  setTeachSearch]  = useState('');
  const [teachModal,   setTeachModal]   = useState(null);
  const [teachCard,    setTeachCard]    = useState('');
  const [teachNote,    setTeachNote]    = useState('');
  const [savingTeach,  setSavingTeach]  = useState(false);

  // RFID Students
  const [rfidStudents, setRFIDStudents] = useState([]);
  const [studSearch,   setStudSearch]   = useState('');
  const [studModal,    setStudModal]    = useState(null);
  const [studCard,     setStudCard]     = useState('');
  const [studNote,     setStudNote]     = useState('');
  const [savingStud,   setSavingStud]   = useState(false);
  const [classes,      setClasses]      = useState([]);
  const [filterClass,  setFilterClass]  = useState('');
  const [filterSection,setFilterSection]= useState('');

  // Config request
  const [configModal, setConfigModal] = useState(false);
  const [configForm,  setConfigForm]  = useState({ ip:'', port:'4370', pollInterval:'60', note:'' });
  const [savingConfig,setSavingConfig] = useState(false);

  // Gate log
  const [gateDate,    setGateDate]    = useState(new Date().toISOString().slice(0,10));
  const [gateData,    setGateData]    = useState(null);
  const [gateLoading, setGateLoading] = useState(false);

  useEffect(() => { fetchStatus(); api.get('/finance/classes').then(r => setClasses(Array.isArray(r.data)?r.data:[])).catch(()=>{}); }, []);
  useEffect(() => { if (activeTab==='rfid-teachers') loadTeachers(); }, [activeTab]);
  useEffect(() => { if (activeTab==='rfid-students') loadStudents(); }, [activeTab, filterClass, filterSection]);
  useEffect(() => { if (activeTab==='gate-log') fetchGateLog(); }, [activeTab, gateDate]);

  const fetchStatus = async () => {
    try { const { data } = await api.get('/finance/biometric/status'); setStatus(data); if (data.config?.ip) setConfigForm(p=>({...p, ip:data.config.ip, port:String(data.config.port||4370), pollInterval:String(data.config.pollInterval||60)})); } catch {}
  };

  const loadTeachers = async () => {
    try { const { data } = await api.get('/finance/biometric/rfid-teachers'); setRFIDTeachers(Array.isArray(data)?data:[]); } catch {}
  };

  const loadStudents = async () => {
    try {
      const params = [];
      if (filterClass)   params.push(`classId=${filterClass}`);
      if (filterSection) params.push(`section=${filterSection}`);
      const { data } = await api.get('/finance/biometric/rfid-students' + (params.length?'?'+params.join('&'):''));
      setRFIDStudents(Array.isArray(data)?data:[]);
    } catch {}
  };

  const fetchGateLog = async () => {
    try {
      setGateLoading(true);
      const { data } = await api.get(`/finance/biometric/gate-log?date=${gateDate}`);
      setGateData(data);
    } catch { toast.error('Failed to load gate log'); }
    finally { setGateLoading(false); }
  };

  const handleStart = async () => {
    setStarting(true);
    try { await api.post('/finance/biometric/start'); toast.success('Polling started'); fetchStatus(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setStarting(false); }
  };

  const handleStop = async () => {
    setStopping(true);
    try { await api.post('/finance/biometric/stop'); toast.success('Polling stopped'); fetchStatus(); }
    catch { toast.error('Failed'); }
    finally { setStopping(false); }
  };

  // Submit RFID teacher request
  const submitTeachRequest = async () => {
    if (!teachCard.trim()) return toast.error('Enter RFID card number');
    if (!teachNote.trim()) return toast.error('Reason is required');
    setSavingTeach(true);
    try {
      await api.post('/finance/biometric/request-rfid-teacher', { teacherId: teachModal._id, rfidCard: teachCard.trim(), note: teachNote });
      toast.success('Request submitted — awaiting admin approval');
      setTeachModal(null); setTeachCard(''); setTeachNote('');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSavingTeach(false); }
  };

  // Submit RFID student request
  const submitStudRequest = async () => {
    if (!studCard.trim()) return toast.error('Enter RFID card number');
    if (!studNote.trim()) return toast.error('Reason is required');
    setSavingStud(true);
    try {
      await api.post('/finance/biometric/request-rfid-student', { studentId: studModal._id, rfidCard: studCard.trim(), note: studNote });
      toast.success('Request submitted — awaiting admin approval');
      setStudModal(null); setStudCard(''); setStudNote('');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSavingStud(false); }
  };

  // Submit config change request
  const submitConfigRequest = async () => {
    if (!configForm.ip.trim()) return toast.error('IP address required');
    if (!configForm.note.trim()) return toast.error('Reason is required');
    setSavingConfig(true);
    try {
      await api.post('/finance/biometric/request-config', { ip: configForm.ip, port: Number(configForm.port), pollInterval: Number(configForm.pollInterval), note: configForm.note });
      toast.success('Config change request submitted — awaiting admin approval');
      setConfigModal(false);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSavingConfig(false); }
  };

  const exportCSV = () => {
    if (!gateData?.entries?.length) return toast.error('No data to export');
    const rows = gateData.entries.map(e => ({ Name: e.student?.user?.name||'', 'Admission No': e.student?.admissionNo||'', Class: e.student?.className||'', Section: e.student?.section||'', 'Entry Time': fmtTime(e.entryTime), 'Card UID': e.cardUID, Source: e.source }));
    const hdrs = Object.keys(rows[0]);
    const csv  = [hdrs.join(','), ...rows.map(r => hdrs.map(h=>r[h]).join(','))].join('\n');
    const blob = new Blob([csv],{type:'text/csv'}); const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=`Gate_Log_${gateDate}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const isPolling    = status?.isPolling;
  const isConnected  = status?.isConnected;
  const lastPull     = status?.lastPullTime ? new Date(status.lastPullTime).toLocaleTimeString() : '—';
  const cfg          = status?.config || {};

  const filtTeachers = rfidTeachers.filter(t => !teachSearch || t.user?.name?.toLowerCase().includes(teachSearch.toLowerCase()) || t.employeeId?.toLowerCase().includes(teachSearch.toLowerCase()));
  const filtStudents = rfidStudents.filter(s => !studSearch  || s.user?.name?.toLowerCase().includes(studSearch.toLowerCase())  || s.admissionNo?.toLowerCase().includes(studSearch.toLowerCase()));

  return (
    <div className="fb"><style>{style}</style>

      <div className="fb-hdr">
        <div><div className="fb-title">Biometric & RFID</div><div className="fb-sub">View RFID assignments, manage gate entries, control polling</div></div>
        <button className="btn btn-ghost btn-sm" onClick={fetchStatus}><ArrowPathIcon />Refresh</button>
      </div>

      {/* Tabs */}
      <div className="page-tabs">
        <button className={`page-tab${activeTab==='status'?       ' active':''}`} onClick={()=>setActiveTab('status')}><FingerPrintIcon />Machine Status</button>
        <button className={`page-tab${activeTab==='rfid-teachers'?' active':''}`} onClick={()=>setActiveTab('rfid-teachers')}><CreditCardIcon />Teacher RFID</button>
        <button className={`page-tab${activeTab==='rfid-students'?' active':''}`} onClick={()=>setActiveTab('rfid-students')}><AcademicCapIcon />Student RFID</button>
        <button className={`page-tab${activeTab==='gate-log'?     ' active':''}`} onClick={()=>setActiveTab('gate-log')}><CalendarDaysIcon />Gate Log</button>
      </div>

      {/* ══ STATUS TAB ══ */}
      {activeTab === 'status' && (
        <>
          <div className={`status-card ${isPolling?'polling':isConnected?'connected':'disconnected'}`}>
            <div className="sc-left">
              <div className={`sc-dot ${isPolling?'blue':isConnected?'green':'red'}`}/>
              <div>
                <div className="sc-label">{isPolling?'🔄 Polling Active':isConnected?'✅ Connected':'❌ Not Connected'}</div>
                <div className="sc-sub">{isPolling?`Syncing every ${cfg.pollInterval||60}s · Last pull: ${lastPull}`:isConnected?'Connected but not polling':'Machine not reachable'}</div>
              </div>
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {!isPolling && <button className="btn btn-teal btn-sm" onClick={handleStart} disabled={starting}><PlayIcon />{starting?'Starting…':'Start Polling'}</button>}
              {isPolling  && <button className="btn btn-red  btn-sm" onClick={handleStop}  disabled={stopping}><StopIcon />{stopping?'Stopping…':'Stop Polling'}</button>}
            </div>
          </div>

          <div className="req-info">
            <strong>Note:</strong> You can start/stop polling directly. To change machine IP, port, or poll interval — submit a config change request below and admin will approve it.
          </div>

          <div className="panel">
            <div className="panel-head">
              <div><div className="panel-title">Machine Configuration</div><div className="panel-sub">Current saved settings</div></div>
              <button className="btn btn-amber btn-sm" onClick={()=>setConfigModal(true)}>Request Config Change</button>
            </div>
            <div style={{padding:'16px 18px',display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:14}}>
              {[['IP Address', cfg.ip||'Not set'], ['Port', cfg.port||4370], ['Poll Interval', `${cfg.pollInterval||60}s`]].map(([lbl,val]) => (
                <div key={lbl}>
                  <div style={{fontSize:'.67rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>{lbl}</div>
                  <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.88rem',fontWeight:600,color:'#0f1123'}}>{val}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ══ RFID TEACHERS TAB ══ */}
      {activeTab === 'rfid-teachers' && (
        <>
          <div className="req-info"><strong>How it works:</strong> Click the assign button to request an RFID card assignment. Admin will approve and the card will be activated.</div>
          <div className="panel">
            <div className="panel-head">
              <div><div className="panel-title">Teacher RFID Cards</div><div className="panel-sub">View assignments · Request changes via approval</div></div>
              <div className="search-wrap">
                <svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input className="search-input" placeholder="Search teachers…" value={teachSearch} onChange={e=>setTeachSearch(e.target.value)}/>
              </div>
            </div>
            {filtTeachers.length===0 ? <div className="empty">No teachers found.</div> : (
              <>
              <div className="fb-tbl-wrap" style={{overflowX:'auto'}}>
                <table className="tbl">
                  <thead><tr><th>Teacher</th><th>Employee ID</th><th>Fingerprint ID</th><th>RFID Card</th><th>Request Change</th></tr></thead>
                  <tbody>
                    {filtTeachers.map(t => (
                      <tr key={t._id}>
                        <td style={{fontWeight:600}}>{t.user?.name}</td>
                        <td><span className="mono">{t.employeeId}</span></td>
                        <td>{t.biometricId?<span style={{fontSize:'.74rem',background:'#f3f0ff',color:'#8b5cf6',padding:'2px 8px',borderRadius:99,fontWeight:600}}>👆 {t.biometricId}</span>:<span className="no-rfid">—</span>}</td>
                        <td>{t.rfidCard?<span className="rfid-badge"><CreditCardIcon/>{t.rfidCard}</span>:<span className="no-rfid">Not assigned</span>}</td>
                        <td>
                          <button className="btn btn-amber btn-sm" onClick={()=>{setTeachModal(t);setTeachCard(t.rfidCard||'');setTeachNote('');}}>
                            <PencilSquareIcon/>{t.rfidCard?'Request Update':'Request Assign'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="fb-cards">
                {filtTeachers.map(t => (
                  <div key={t._id} className="fb-card">
                    <div className="fbc-top">
                      <span style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123'}}>{t.user?.name}</span>
                      {t.rfidCard?<span className="rfid-badge"><CreditCardIcon/>{t.rfidCard}</span>:<span className="no-rfid">Not assigned</span>}
                    </div>
                    <div className="fbc-row"><span className="fbc-label">Employee ID</span><span className="mono">{t.employeeId}</span></div>
                    <div className="fbc-row"><span className="fbc-label">Fingerprint ID</span>{t.biometricId?<span style={{fontSize:'.74rem',background:'#f3f0ff',color:'#8b5cf6',padding:'2px 8px',borderRadius:99,fontWeight:600}}>👆 {t.biometricId}</span>:<span className="no-rfid">—</span>}</div>
                    <div className="fbc-actions">
                      <button className="btn btn-amber btn-sm" style={{width:'100%',justifyContent:'center'}} onClick={()=>{setTeachModal(t);setTeachCard(t.rfidCard||'');setTeachNote('');}}>
                        <PencilSquareIcon/>{t.rfidCard?'Request Update':'Request Assign'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ══ RFID STUDENTS TAB ══ */}
      {activeTab === 'rfid-students' && (
        <>
          <div className="req-info"><strong>How it works:</strong> Click assign to request an RFID card assignment for a student. Admin approval required.</div>
          <div className="panel">
            <div className="panel-head">
              <div><div className="panel-title">Student RFID Cards</div><div className="panel-sub">View assignments · Request changes via approval</div></div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                <select style={{fontFamily:'Sora,sans-serif',fontSize:'.78rem',padding:'7px 11px',border:'1.5px solid #e0e4f0',borderRadius:9,background:'#f8f9fc',outline:'none'}}
                  value={filterClass} onChange={e=>{setFilterClass(e.target.value);setFilterSection('');}}>
                  <option value="">All Classes</option>
                  {sortByName(classes).map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
                <select style={{fontFamily:'Sora,sans-serif',fontSize:'.78rem',padding:'7px 11px',border:'1.5px solid #e0e4f0',borderRadius:9,background:'#f8f9fc',outline:'none'}}
                  value={filterSection} onChange={e=>setFilterSection(e.target.value)} disabled={!filterClass}>
                  <option value="">All Sections</option>
                  {filterClass && sortAsc(classes.find(c=>String(c._id)===String(filterClass))?.sections||[]).map(s=><option key={s} value={s}>{s}</option>)}
                </select>
                <div className="search-wrap">
                  <svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                  <input className="search-input" placeholder="Search students…" value={studSearch} onChange={e=>setStudSearch(e.target.value)}/>
                </div>
              </div>
            </div>
            {filtStudents.length===0 ? <div className="empty">No students found.</div> : (
              <>
              <div className="fb-tbl-wrap" style={{overflowX:'auto'}}>
                <table className="tbl">
                  <thead><tr><th>Student</th><th>Admission No</th><th>Class</th><th>Section</th><th>RFID Card</th><th>Request Change</th></tr></thead>
                  <tbody>
                    {filtStudents.map(s => (
                      <tr key={s._id}>
                        <td style={{fontWeight:600}}>{s.user?.name}</td>
                        <td><span className="mono">{s.admissionNo}</span></td>
                        <td><span style={{fontSize:'.74rem',background:'#f3f0ff',color:'#8b5cf6',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{s.class?.name||'—'}</span></td>
                        <td><span style={{fontSize:'.74rem',background:'#eef2ff',color:'#3b6ef8',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{s.section}</span></td>
                        <td>{s.rfidCard?<span className="rfid-badge"><CreditCardIcon/>{s.rfidCard}</span>:<span className="no-rfid">Not assigned</span>}</td>
                        <td>
                          <button className="btn btn-amber btn-sm" onClick={()=>{setStudModal(s);setStudCard(s.rfidCard||'');setStudNote('');}}>
                            <PencilSquareIcon/>{s.rfidCard?'Request Update':'Request Assign'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="fb-cards">
                {filtStudents.map(s => (
                  <div key={s._id} className="fb-card">
                    <div className="fbc-top">
                      <span style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123'}}>{s.user?.name}</span>
                      {s.rfidCard?<span className="rfid-badge"><CreditCardIcon/>{s.rfidCard}</span>:<span className="no-rfid">Not assigned</span>}
                    </div>
                    <div className="fbc-row"><span className="fbc-label">Admission No</span><span className="mono">{s.admissionNo}</span></div>
                    <div className="fbc-row"><span className="fbc-label">Class</span><span style={{fontSize:'.74rem',background:'#f3f0ff',color:'#8b5cf6',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{s.class?.name||'—'}</span></div>
                    <div className="fbc-row"><span className="fbc-label">Section</span><span style={{fontSize:'.74rem',background:'#eef2ff',color:'#3b6ef8',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{s.section}</span></div>
                    <div className="fbc-actions">
                      <button className="btn btn-amber btn-sm" style={{width:'100%',justifyContent:'center'}} onClick={()=>{setStudModal(s);setStudCard(s.rfidCard||'');setStudNote('');}}>
                        <PencilSquareIcon/>{s.rfidCard?'Request Update':'Request Assign'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ══ GATE LOG TAB ══ */}
      {activeTab === 'gate-log' && (
        <>
          <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:'1.2rem'}}>
            <input type="date" className="gl-date" value={gateDate} onChange={e=>setGateDate(e.target.value)}/>
            <button className="btn btn-ghost btn-sm" onClick={exportCSV}><ArrowDownTrayIcon/>Export CSV</button>
          </div>
          <div className="stat-grid">
            <div className="stat-card"><div className="sc-icon teal"><UserGroupIcon/></div><div className="sc-lbl">Students Entered</div><div className="sc-val">{gateData?.uniqueStudents||0}</div></div>
            <div className="stat-card"><div className="sc-icon blue"><CalendarDaysIcon/></div><div className="sc-lbl">Total Taps</div><div className="sc-val">{gateData?.total||0}</div></div>
            <div className="stat-card"><div className="sc-icon amber"><ClockIcon/></div><div className="sc-lbl">First Entry</div><div className="sc-val" style={{fontSize:'1rem'}}>{gateData?.entries?.[0]?.entryTime ? fmtTime(gateData.entries[0].entryTime) : '—'}</div></div>
          </div>
          <div className="panel">
            <div className="panel-head">
              <div><div className="panel-title">Entry Log — {fmtDate(gateDate)}</div><div className="panel-sub">{gateLoading?'Loading…':`${gateData?.total||0} entries · ${gateData?.uniqueStudents||0} unique students`}</div></div>
            </div>
            {gateLoading ? <div className="loading"><div className="spinner"/>Loading…</div>
            : !gateData?.entries?.length ? <div className="empty">No entries for this date.</div>
            : <>
              <div className="fb-tbl-wrap" style={{overflowX:'auto'}}>
                <table className="tbl">
                  <thead><tr><th>#</th><th>Student</th><th>Admission No</th><th>Class</th><th>Section</th><th>Entry Time</th><th>Card UID</th></tr></thead>
                  <tbody>
                    {gateData.entries.map((e,i) => (
                      <tr key={e._id}>
                        <td style={{color:'#c0c5dc',fontSize:'.72rem'}}>{i+1}</td>
                        <td><div className="name-cell"><div className="avatar">{ini(e.student?.user?.name)}</div><div style={{fontWeight:600}}>{e.student?.user?.name||'—'}</div></div></td>
                        <td><span className="mono">{e.student?.admissionNo||'—'}</span></td>
                        <td><span style={{fontSize:'.74rem',background:'#f3f0ff',color:'#8b5cf6',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{e.student?.className||'—'}</span></td>
                        <td><span style={{fontSize:'.74rem',background:'#eef2ff',color:'#3b6ef8',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{e.student?.section||'—'}</span></td>
                        <td><span className="time-badge"><ClockIcon/>{fmtTime(e.entryTime)}</span></td>
                        <td><span className="mono">{e.cardUID}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="fb-cards">
                {gateData.entries.map((e,i) => (
                  <div key={e._id} className="fb-card">
                    <div className="fbc-top">
                      <div className="name-cell"><div className="avatar">{ini(e.student?.user?.name)}</div><div style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123'}}>{e.student?.user?.name||'—'}</div></div>
                      <span className="time-badge"><ClockIcon/>{fmtTime(e.entryTime)}</span>
                    </div>
                    <div className="fbc-row"><span className="fbc-label">#</span><span style={{color:'#c0c5dc',fontSize:'.72rem'}}>{i+1}</span></div>
                    <div className="fbc-row"><span className="fbc-label">Admission No</span><span className="mono">{e.student?.admissionNo||'—'}</span></div>
                    <div className="fbc-row"><span className="fbc-label">Class</span><span style={{fontSize:'.74rem',background:'#f3f0ff',color:'#8b5cf6',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{e.student?.className||'—'}</span></div>
                    <div className="fbc-row"><span className="fbc-label">Section</span><span style={{fontSize:'.74rem',background:'#eef2ff',color:'#3b6ef8',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{e.student?.section||'—'}</span></div>
                    <div className="fbc-row"><span className="fbc-label">Card UID</span><span className="mono">{e.cardUID}</span></div>
                  </div>
                ))}
              </div>
              </>
            }
          </div>
        </>
      )}

      {/* ── Teacher RFID request modal ── */}
      {teachModal && createPortal(
        <div className="overlay" onClick={()=>setTeachModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="mo-hdr"><div className="mo-title">Request RFID Assignment</div><button className="mo-close" onClick={()=>setTeachModal(null)}><XMarkIcon/></button></div>
            <div className="mo-body">
              <div className="ctx-box"><div className="ctx-name">{teachModal.user?.name}</div><div className="ctx-sub">{teachModal.employeeId} {teachModal.rfidCard?`· Current card: ${teachModal.rfidCard}`:''}</div></div>
              <div className="ff"><label className="fl">New RFID Card Number *</label><input className="fi mono" value={teachCard} onChange={e=>setTeachCard(e.target.value)} placeholder="e.g. 3"/><span className="fh">User ID assigned on the machine</span></div>
              <div className="ff"><label className="fl">Reason</label><input className="fi" required value={teachNote} onChange={e=>setTeachNote(e.target.value)} placeholder="Reason for change…"/></div>
              <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:9,padding:'10px 12px',fontSize:'.75rem',color:'#92400e'}}>⚠️ This will be sent to admin for approval before taking effect.</div>
            </div>
            <div className="mo-foot">
              <button className="btn btn-ghost" onClick={()=>setTeachModal(null)}>Cancel</button>
              <button className="btn btn-amber" onClick={submitTeachRequest} disabled={savingTeach}>{savingTeach?'Submitting…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Student RFID request modal ── */}
      {studModal && createPortal(
        <div className="overlay" onClick={()=>setStudModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="mo-hdr"><div className="mo-title">Request RFID Assignment</div><button className="mo-close" onClick={()=>setStudModal(null)}><XMarkIcon/></button></div>
            <div className="mo-body">
              <div className="ctx-box"><div className="ctx-name">{studModal.user?.name}</div><div className="ctx-sub">{studModal.admissionNo} · {studModal.class?.name} {studModal.section}{studModal.rfidCard?` · Current card: ${studModal.rfidCard}`:''}</div></div>
              <div className="ff"><label className="fl">New RFID Card Number *</label><input className="fi mono" value={studCard} onChange={e=>setStudCard(e.target.value)} placeholder="e.g. 3"/><span className="fh">User ID assigned on the machine</span></div>
              <div className="ff"><label className="fl">Reason</label><input className="fi" required value={studNote} onChange={e=>setStudNote(e.target.value)} placeholder="Reason for change…"/></div>
              <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:9,padding:'10px 12px',fontSize:'.75rem',color:'#92400e'}}>⚠️ This will be sent to admin for approval before taking effect.</div>
            </div>
            <div className="mo-foot">
              <button className="btn btn-ghost" onClick={()=>setStudModal(null)}>Cancel</button>
              <button className="btn btn-amber" onClick={submitStudRequest} disabled={savingStud}>{savingStud?'Submitting…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Config change request modal ── */}
      {configModal && createPortal(
        <div className="overlay" onClick={()=>setConfigModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="mo-hdr"><div className="mo-title">Request Config Change</div><button className="mo-close" onClick={()=>setConfigModal(false)}><XMarkIcon/></button></div>
            <div className="mo-body">
              <div className="ff"><label className="fl">Machine IP Address *</label><input className="fi mono" value={configForm.ip} onChange={e=>setConfigForm(p=>({...p,ip:e.target.value}))} placeholder="192.168.100.201"/></div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div className="ff"><label className="fl">Port</label><input className="fi mono" value={configForm.port} onChange={e=>setConfigForm(p=>({...p,port:e.target.value}))} placeholder="4370"/></div>
                <div className="ff"><label className="fl">Poll Interval (s)</label><input type="number" className="fi mono" value={configForm.pollInterval} onChange={e=>setConfigForm(p=>({...p,pollInterval:e.target.value}))} placeholder="60" min="10"/></div>
              </div>
              <div className="ff"><label className="fl">Reason / Note</label><input className="fi" required value={configForm.note} onChange={e=>setConfigForm(p=>({...p,note:e.target.value}))} placeholder="Why is this change needed?"/></div>
              <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:9,padding:'10px 12px',fontSize:'.75rem',color:'#92400e'}}>⚠️ Admin approval required. Machine will restart polling with new settings when approved.</div>
            </div>
            <div className="mo-foot">
              <button className="btn btn-ghost" onClick={()=>setConfigModal(false)}>Cancel</button>
              <button className="btn btn-amber" onClick={submitConfigRequest} disabled={savingConfig}>{savingConfig?'Submitting…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}